import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Plus, Send, Paperclip, Loader2, X, Trash2, RotateCcw, Sparkles, Link2, Copy, Check, ChevronRight, ChevronDown, Users,
} from 'lucide-react';
import MicButton from '../components/MicButton';
import { askApi } from '../services/api';

// Ask: staff ask a question - text, a pasted screenshot, a forwarded handoff - and go
// back to work. Claude answers from the knowledge base, read-only CPx data and the
// person's own mailbox (netlify/functions/lib/ask-tools.js), and emails them a link if
// they have left the page by the time it is done.
//
// ⚠️ NOT THE CLAUDE CODE BUTTON. That one changes the app; this one only reads, saves
// notes for Greg to review and files change requests. That is why every CPx user gets it.
//
// The same toolbox is available inside the Claude app through the connector - the
// "Connect your Claude app" button makes the person's private link.

const MAX_IMAGES = 6;
const MAX_EDGE = 1600;

const TIER_LABEL = {
  staff: 'Operations data',
  finance: 'Operations + the books',
  payroll: 'Everything incl. payroll',
};

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    if (!/^image\//.test(file.type)) { reject(new Error(`${file.name || 'That file'} is not an image. Paste a screenshot or copy the text in.`)); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      const dataUrl = c.toDataURL('image/jpeg', 0.85);
      resolve({ media_type: 'image/jpeg', data: dataUrl.split(',')[1], preview: dataUrl, name: file.name || 'screenshot' });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image.')); };
    img.src = url;
  });
}

function when(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const S = {
  wrap: { display: 'flex', height: 'calc(100vh - 120px)', minHeight: 520, background: '#fff', border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 10, overflow: 'hidden' },
  side: { width: 270, borderRight: '1px solid var(--gray-200, #e5e7eb)', display: 'flex', flexDirection: 'column', background: '#f9fafb', flexShrink: 0 },
  sideHead: { padding: 12, display: 'flex', gap: 8, borderBottom: '1px solid var(--gray-200, #e5e7eb)' },
  newBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', border: 'none', borderRadius: 7, background: 'var(--orange, #dc582a)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  list: { flex: 1, overflowY: 'auto' },
  item: (on) => ({ padding: '10px 12px', borderBottom: '1px solid #eef0f2', cursor: 'pointer', background: on ? '#fff' : 'transparent', borderLeft: on ? '3px solid var(--orange, #dc582a)' : '3px solid transparent' }),
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  head: { padding: '12px 18px', borderBottom: '1px solid var(--gray-200, #e5e7eb)', display: 'flex', alignItems: 'center', gap: 12 },
  thread: { flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 },
  bubble: (user) => ({
    alignSelf: user ? 'flex-end' : 'stretch', maxWidth: user ? '78%' : '100%',
    background: user ? '#003b5c' : '#fff', color: user ? '#fff' : '#1f2937',
    border: user ? 'none' : '1px solid var(--gray-200, #e5e7eb)', borderRadius: 10, padding: '10px 14px', fontSize: 14, lineHeight: 1.55,
  }),
  composer: { borderTop: '1px solid var(--gray-200, #e5e7eb)', padding: 12 },
  input: { flex: 1, resize: 'none', border: '1px solid var(--gray-300, #d1d5db)', borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', minHeight: 48, maxHeight: 220 },
  iconBtn: { border: 'none', background: 'transparent', cursor: 'pointer', padding: 6, color: '#4b5563', display: 'inline-flex', alignItems: 'center' },
  send: (dis) => ({ border: 'none', borderRadius: 8, padding: '0 14px', height: 40, background: dis ? '#cbd5e1' : 'var(--orange, #dc582a)', color: '#fff', cursor: dis ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center' }),
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#eef2f7', color: '#334155' },
  secondary: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: '1px solid var(--gray-300, #d1d5db)', borderRadius: 7, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#003b5c', fontWeight: 600 },
};

function Markdown({ text, light }) {
  return (
    <div className={`ask-md${light ? ' ask-md-light' : ''}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
        a: ({ node, children, ...p }) => <a {...p} target="_blank" rel="noreferrer">{children}</a>,
      }}>{text || ''}</ReactMarkdown>
    </div>
  );
}

function ToolLog({ log }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(log) || !log.length) return null;
  const label = (l) => {
    if (l.tool === 'query_data') return `Data: ${l.purpose || 'query'}`;
    if (l.tool === 'search_kb') return `Searched the knowledge base for "${l.query}"`;
    if (l.tool === 'read_kb') return `Read ${l.path}`;
    if (l.tool === 'search_my_email') return `Searched your email: ${l.query}`;
    if (l.tool === 'read_my_email') return 'Read an email';
    if (l.tool === 'draft_reply') return `Put a reply${l.reply_all ? ' (reply all)' : ''} in your Outlook Drafts - not sent`;
    if (l.tool === 'save_note') return `Saved a note for review: ${l.title}`;
    if (l.tool === 'save_my_note') return `Saved to your notes: ${l.title}`;
    if (l.tool === 'update_my_note') return `Updated your note${l.title ? ': ' + l.title : ''}`;
    if (l.tool === 'delete_my_note') return 'Deleted one of your notes';
    if (l.tool === 'list_my_notes') return 'Looked at your notes';
    if (l.tool === 'list_playbook') return 'Looked at the customer service playbook';
    if (l.tool === 'edit_playbook') return `Updated the playbook: ${l.summary || l.slug}`;
    if (l.tool === 'request_change') return `Filed a change request: ${l.summary}`;
    if (l.tool === 'describe_table') return `Looked at table ${l.table}`;
    if (l.tool === 'list_tables') return 'Listed data tables';
    return l.tool;
  };
  return (
    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
      <button style={{ ...S.iconBtn, padding: 0, fontSize: 12, color: '#6b7280' }} onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} What I looked at ({log.length})
      </button>
      {open && (
        <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
          {log.map((l, i) => (
            <li key={i} style={{ marginBottom: 3 }}>
              {label(l)}
              {l.sql && <pre style={{ whiteSpace: 'pre-wrap', background: '#f3f4f6', padding: 6, borderRadius: 4, fontSize: 11, margin: '3px 0' }}>{l.sql}</pre>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Working({ since }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(t); }, []);
  const secs = since ? Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 1000)) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280', fontSize: 13 }}>
      <Loader2 size={15} className="cb-spin" style={{ animation: 'spin 1s linear infinite' }} />
      Working on it{secs > 5 ? ` · ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}` : ''}. You can leave this page; you will get an email when it is answered.
    </div>
  );
}

function ConnectModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { askApi('connector-status').then(setStatus).catch(() => setStatus({ active: false })); }, []);
  const make = async () => {
    setBusy(true); setErr('');
    try { const r = await askApi('connector-key'); setUrl(r.url); setStatus({ active: true, created_at: new Date().toISOString() }); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const off = async () => {
    setBusy(true);
    try { await askApi('connector-off'); setStatus({ active: false }); setUrl(''); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const copy = () => { navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 560, width: '100%', padding: 22, boxShadow: '0 20px 50px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, flex: 1, color: '#003b5c' }}>Use CPx inside the Claude app</h3>
          <button style={S.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>
        <p style={{ fontSize: 14, color: '#374151', marginTop: 0 }}>
          This gives the Claude app (claude.ai, the desktop app or the phone app) the same tools as this page:
          the knowledge base, your data, your email, your own notes and filing requests. Then you can ask from
          any Claude chat and run several at once.
        </p>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 4 }}><strong>On the Crab Place Claude team (most people):</strong></p>
        <ol style={{ fontSize: 14, color: '#374151', paddingLeft: 20, lineHeight: 1.6, marginTop: 0 }}>
          <li>In Claude, open <strong>Settings → Connectors</strong> and find <strong>CPx</strong>.</li>
          <li>Click <strong>Connect</strong>. A CPx page opens; click <strong>Connect</strong> there (sign in to CPx first if it asks).</li>
          <li>In a chat, make sure CPx is switched on in the tools menu, then ask away.</li>
        </ol>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 4 }}><strong>On your own Claude plan:</strong></p>
        <ol style={{ fontSize: 14, color: '#374151', paddingLeft: 20, lineHeight: 1.6, marginTop: 0 }}>
          <li>Make your private link below and copy it.</li>
          <li>In Claude, open <strong>Settings → Connectors → Add custom connector</strong>.</li>
          <li>Name it <strong>CPx</strong>, paste the link as the URL, and click <strong>Add</strong>.</li>
          <li>In a chat, make sure CPx is switched on in the tools menu, then ask away.</li>
        </ol>
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 10, fontSize: 13, color: '#7c2d12', marginBottom: 14 }}>
          The link <strong>is</strong> your login. Don't share it or paste it anywhere else. If it gets out, press
          "Make a new link". That switches the old one off straight away.
        </div>
        {url ? (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input readOnly value={url} style={{ ...S.input, minHeight: 0, height: 38, fontSize: 12 }} onFocus={(e) => e.target.select()} />
            <button style={S.secondary} onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copied' : 'Copy'}</button>
          </div>
        ) : status && status.active ? (
          <p style={{ fontSize: 13, color: '#374151' }}>
            You have a live link (made {when(status.created_at)}{status.last_used_at ? `, last used ${when(status.last_used_at)}` : ', not used yet'}).
            For safety it is only shown once. Make a new one if you need it again.
          </p>
        ) : null}
        {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...S.newBtn, flex: 'none', padding: '9px 16px' }} disabled={busy} onClick={make}>
            {busy ? <Loader2 size={14} /> : <Link2 size={14} />} {status && status.active ? 'Make a new link' : 'Make my link'}
          </button>
          {status && status.active && <button style={S.secondary} disabled={busy} onClick={off}>Switch it off</button>}
        </div>
      </div>
    </div>
  );
}

export default function Ask() {
  const [threads, setThreads] = useState([]);
  const [me, setMe] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [activeId, setActiveId] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('thread');
    return t ? Number(t) : null;
  });
  const [thread, setThread] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [imgs, setImgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [connect, setConnect] = useState(false);
  const [micStop, setMicStop] = useState(0);
  const [dragging, setDragging] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const imgCache = useRef({});

  const working = thread && (thread.status === 'queued' || thread.status === 'running');

  const loadThreads = useCallback(async () => {
    try {
      const r = await askApi('threads', { all: showAll });
      setThreads(r.threads || []);
      setMe(r.me || null);
    } catch (e) { setErr(e.message); }
  }, [showAll]);

  const loadThread = useCallback(async (id, withImages) => {
    if (!id) { setThread(null); setMsgs([]); return; }
    try {
      const r = await askApi('thread', { id, withImages: !!withImages });
      setThread(r.thread);
      setMsgs((r.messages || []).map((m) => {
        if (m.attachments) imgCache.current[m.id] = m.attachments;
        return { ...m, attachments: m.attachments || imgCache.current[m.id] || null };
      }));
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadThread(activeId, true); }, [activeId, loadThread]);

  // Poll the open thread while Claude is working; refresh the list now and then.
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(() => loadThread(activeId, false), 3000);
    return () => clearInterval(t);
  }, [working, activeId, loadThread]);
  useEffect(() => {
    const t = setInterval(loadThreads, 20000);
    return () => clearInterval(t);
  }, [loadThreads]);
  useEffect(() => {
    if (thread && thread.status === 'done') loadThreads();
  }, [thread && thread.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length, working]);

  const addFiles = async (files) => {
    setErr('');
    for (const f of files) {
      try {
        const im = await fileToImage(f);
        setImgs((p) => (p.length >= MAX_IMAGES ? p : [...p, im]));
      } catch (e) { setErr(e.message); }
    }
  };

  const send = async () => {
    if (busy || working) return;
    const body = text.trim();
    if (!body && !imgs.length) return;
    setBusy(true); setErr(''); setMicStop((n) => n + 1);
    try {
      const r = await askApi('send', {
        threadId: activeId || undefined,
        text: body,
        attachments: imgs.map(({ media_type, data }) => ({ media_type, data })),
      });
      setText(''); setImgs([]);
      if (r.threadId !== activeId) setActiveId(r.threadId);
      else await loadThread(r.threadId, true);
      loadThreads();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const retry = async () => {
    try { await askApi('retry', { id: activeId }); await loadThread(activeId, false); } catch (e) { setErr(e.message); }
  };
  const archive = async (id) => {
    try { await askApi('archive', { id }); if (id === activeId) setActiveId(null); loadThreads(); } catch (e) { setErr(e.message); }
  };
  const takeDictation = useCallback((chunk) => setText((p) => (p ? `${p} ${chunk}` : chunk)), []);

  const mine = (t) => !me || t.staff_name === me.staff_name;

  return (
    <div style={{ padding: '16px 20px' }}>
      <style>{`
        .ask-md p { margin: 0 0 8px; } .ask-md p:last-child { margin-bottom: 0; }
        .ask-md table { border-collapse: collapse; margin: 8px 0; font-size: 13px; display: block; overflow-x: auto; }
        .ask-md th, .ask-md td { border: 1px solid #e5e7eb; padding: 4px 8px; text-align: left; }
        .ask-md th { background: #f3f4f6; }
        .ask-md ul, .ask-md ol { margin: 4px 0 8px; padding-left: 22px; }
        .ask-md code { background: #f3f4f6; padding: 1px 4px; border-radius: 3px; font-size: 12.5px; }
        .ask-md-light a { color: #fed7aa; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, color: '#003b5c', display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={20} color="#dc582a" /> Ask</h2>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            Ask anything about how we work, a customer, an order or a report. Paste a screenshot, or mention an email in your inbox.
            Send it and get back to work: the answer lands here and in your email.
          </div>
        </div>
        {me && <span style={S.chip} title="What data Ask can read for you">{TIER_LABEL[me.tier] || me.tier}</span>}
        <a style={{ ...S.secondary, textDecoration: 'none' }} href="/?page=ask-page">My pages</a>
        <button style={S.secondary} onClick={() => setConnect(true)}><Link2 size={14} /> Connect your Claude app</button>
      </div>

      <div style={S.wrap}>
        <aside style={S.side}>
          <div style={S.sideHead}>
            <button style={S.newBtn} onClick={() => { setActiveId(null); setThread(null); setMsgs([]); setTimeout(() => inputRef.current?.focus(), 0); }}>
              <Plus size={15} /> New question
            </button>
            {me && me.isGreg && (
              <button title={showAll ? "Show only mine" : "Show everyone's"} style={{ ...S.iconBtn, border: '1px solid #d1d5db', borderRadius: 7, background: showAll ? '#003b5c' : '#fff', color: showAll ? '#fff' : '#4b5563' }} onClick={() => setShowAll(!showAll)}>
                <Users size={15} />
              </button>
            )}
          </div>
          <div style={S.list}>
            {!threads.length && <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>No questions yet.</div>}
            {threads.map((t) => (
              <div key={t.id} style={S.item(t.id === activeId)} onClick={() => setActiveId(t.id)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{t.title}</div>
                  {mine(t) && (
                    <button title="Remove" style={{ ...S.iconBtn, padding: 2, color: '#9ca3af' }} onClick={(e) => { e.stopPropagation(); archive(t.id); }}><Trash2 size={12} /></button>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 3, display: 'flex', gap: 6, alignItems: 'center' }}>
                  {(t.status === 'queued' || t.status === 'running') && <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />}
                  {t.status === 'error' && <span style={{ color: '#b91c1c' }}>Failed ·</span>}
                  {!mine(t) && <span>{t.staff_name} ·</span>}
                  {when(t.updated_at)}
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main
          style={{ ...S.main, outline: dragging ? '2px dashed #dc582a' : 'none', outlineOffset: -6 }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={async (e) => { e.preventDefault(); setDragging(false); await addFiles(Array.from(e.dataTransfer.files || [])); }}
        >
          <div style={S.head}>
            <div style={{ flex: 1, minWidth: 0, fontWeight: 700, color: '#003b5c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {thread ? thread.title : 'New question'}
            </div>
            {thread && thread.staff_name && me && thread.staff_name !== me.staff_name && <span style={S.chip}>{thread.staff_name}</span>}
          </div>

          <div style={S.thread}>
            {!msgs.length && (
              <div style={{ margin: 'auto', textAlign: 'center', color: '#6b7280', maxWidth: 520, fontSize: 14 }}>
                <Sparkles size={26} color="#dc582a" />
                <p style={{ fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>What do you need?</p>
                <p style={{ marginTop: 0 }}>Some things to try:</p>
                <div style={{ textAlign: 'left', display: 'inline-block', lineHeight: 1.8 }}>
                  • "Look at Rachael's email about UPS Access Point imports, summarize it, tell me if I need to do anything, and write her back"<br />
                  • "How many VIP members renewed last month vs the month before?"<br />
                  • "What's our rule on refunding a late delivery?"<br />
                  • Paste a screenshot and ask "what's going on here?"<br />
                  • "Remember this: …" saves a note to the knowledge base (Greg approves it first)
                </div>
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={S.bubble(m.role === 'user')}>
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: m.content ? 8 : 0 }}>
                      {m.attachments.map((a, i) => (
                        <img key={i} alt="" src={`data:${a.media_type};base64,${a.data}`} style={{ maxHeight: 140, maxWidth: 220, borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)' }} />
                      ))}
                    </div>
                  )}
                  {!m.attachments && m.image_count > 0 && <div style={{ fontSize: 12, opacity: 0.8 }}>{m.image_count} screenshot{m.image_count > 1 ? 's' : ''}</div>}
                  {m.role === 'user' ? <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div> : <Markdown text={m.content} />}
                  {m.role === 'assistant' && <ToolLog log={m.tool_log} />}
                </div>
              </div>
            ))}
            {working && <Working since={thread.updated_at} />}
            {thread && thread.status === 'error' && (
              <div style={{ color: '#b91c1c', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                That one failed: {thread.error || 'unknown error'}
                <button style={S.secondary} onClick={retry}><RotateCcw size={13} /> Try again</button>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {err && <div style={{ color: '#b91c1c', fontSize: 13, padding: '0 14px 6px' }}>{err}</div>}

          <div style={S.composer}>
            {!!imgs.length && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                {imgs.map((im, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img alt="" src={im.preview} style={{ height: 64, borderRadius: 6, border: '1px solid #d1d5db' }} />
                    <button onClick={() => setImgs((p) => p.filter((_, n) => n !== i))} style={{ position: 'absolute', top: -6, right: -6, background: '#111827', color: '#fff', border: 'none', borderRadius: 999, width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                style={S.input}
                rows={2}
                value={text}
                disabled={working}
                placeholder={working ? 'Working on your last message. It will land above when done.' : (activeId ? 'Reply or ask a follow-up…' : 'Ask a question, paste a screenshot, or mention an email…')}
                onChange={(e) => setText(e.target.value)}
                onPaste={async (e) => {
                  const files = Array.from(e.clipboardData?.files || []);
                  if (files.length) { e.preventDefault(); await addFiles(files); }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              />
              <MicButton onTranscript={takeDictation} stopWhen={micStop} size={16} />
              <button style={S.iconBtn} title="Attach a screenshot" onClick={() => fileRef.current?.click()}><Paperclip size={17} /></button>
              <button style={S.send(busy || working || (!text.trim() && !imgs.length))} onClick={send} disabled={busy || working} title="Send">
                {busy ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={async (e) => { await addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
            <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6 }}>
              Enter sends, Shift+Enter for a new line. Ask can read but not change anything. Replies go to your Outlook Drafts for you to send; requests for changes go to Greg's board.
            </div>
          </div>
        </main>
      </div>
      {connect && <ConnectModal onClose={() => setConnect(false)} />}
    </div>
  );
}
